import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle, XCircle } from "lucide-react";

/**
 * Handles the redirect from Google OAuth for Gmail connector.
 * Expected URL params: ?status=success&credential_id=...
 */
const GmailAuthHandler: React.FC = () => {
  const [authStatus, setAuthStatus] = useState<{
    status: string | null;
    error: string | null;
  }>({ status: null, error: null });

  useEffect(() => {
    const closeWindow = (delay: number = 1500) => {
      setTimeout(() => window.close(), delay);
    };

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get("status");
      const credentialId = urlParams.get("credential_id");
      const error = urlParams.get("error");

      setAuthStatus({ status, error });

      if (status === "success" && credentialId) {
        // Notify the parent window (workflow editor)
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "GOOGLE_AUTH_SUCCESS",
              status: "success",
              credential_id: credentialId,
              timestamp: Date.now(),
            },
            "*"
          );
        }

        // Close window after showing success message
        closeWindow(2000);
      } else {
        // Handle error case
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "GOOGLE_AUTH_ERROR",
              error: error || "Authentication failed or missing credential ID",
              timestamp: Date.now(),
            },
            "*"
          );
        }

        // Don't close immediately on error so user can read it
        // closeWindow(3000);
      }
    } catch (err) {
      console.error("Gmail Auth handler error:", err);
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "GOOGLE_AUTH_ERROR",
            error: "Failed to process authentication callback",
            timestamp: Date.now(),
          },
          "*"
        );
      }
    }
  }, []);

  const { status, error } = authStatus;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-xl border border-gray-100 p-8 max-w-sm w-full text-center">
        {status === "success" ? (
          <div className="animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Connected to Gmail
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Your account has been successfully linked. You can close this
              window now.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Closing window...
            </div>
          </div>
        ) : (
          <div className="animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Connection Failed
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {error || "Unable to complete the authentication process."}
            </p>
            <button
              onClick={() => window.close()}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
            >
              Close Window
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GmailAuthHandler;
